/*
 * F-04 · EL BLOQUEO DE LA BAJA SE ANTICIPA EN LA PANTALLA (RF-181, RF-182,
 * RN-545 a RN-548b).
 *
 * Traduce a castellano el par `puedeDesactivarse` / `motivoNoDesactivable` que YA
 * viaja en `CuentaDto`. No se crea endpoint, no se agrega consulta y el dato no se
 * pide dos veces.
 *
 * DESHABILITAR EL BOTÓN NO ES EL CONTROL (RN-548a): el servidor rechaza la baja
 * igual si la petición llega por otra vía. Esto es anticipación visual, para que
 * la persona no pulse un botón habilitado y reciba un error.
 */

import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';

/**
 * Los TRES motivos cerrados. Son valores que ya existen en el enumerado de errores
 * del servidor: este incremento no agrega ni un identificador (RF-182, CA-11-20a).
 */
export const MOTIVOS_DE_BAJA = Object.freeze({
  CUENTA_CON_SALDO: 'CUENTA_CON_SALDO',
  CONSUMIDOR_NO_DISPONIBLE: 'CONSUMIDOR_NO_DISPONIBLE',
});

/** Qué hace la pantalla con la acción de baja, en los cuatro estados del par. */
export const PRESENTACION_DE_LA_BAJA = Object.freeze({
  HABILITADA: 'HABILITADA',
  DESHABILITADA: 'DESHABILITADA',
  // Ni habilitada ni deshabilitada: la acción NO SE DIBUJA. La respuesta no trae
  // saldos, así que el falso no significa «tiene saldo» (RN-548).
  AUSENTE: 'AUSENTE',
});

/**
 * Resuelve qué presentar, con qué texto y qué atajo.
 *
 * @param cuenta el `CuentaDto` tal como llegó.
 * @returns {{presentacion:string, texto:string|null, salida:string|null,
 *            rotuloDelAtajo:string|null, ofreceReintento:boolean}}
 */
export function resolverBajaDeCuenta(cuenta) {
  if (!cuenta) return { presentacion: PRESENTACION_DE_LA_BAJA.AUSENTE, texto: null, salida: null, rotuloDelAtajo: null, ofreceReintento: false };

  if (cuenta.puedeDesactivarse) {
    return {
      presentacion: PRESENTACION_DE_LA_BAJA.HABILITADA,
      texto: null,
      salida: null,
      rotuloDelAtajo: null,
      ofreceReintento: false,
    };
  }

  // Falso con motivo NULO: la respuesta no trae saldos. Deshabilitar con una razón
  // adivinada sería mentir, y habilitar sería peor (RN-548).
  if (!cuenta.motivoNoDesactivable) {
    return {
      presentacion: PRESENTACION_DE_LA_BAJA.AUSENTE,
      texto: null,
      salida: null,
      rotuloDelAtajo: null,
      ofreceReintento: false,
    };
  }

  // El único motivo que NO depende del dinero sino de que el módulo de Movimientos
  // respondió mal. Nunca se asume cero: fallar del lado seguro es lo único
  // compatible con una regla que existe para no perder dinero de vista (RN-547).
  if (cuenta.motivoNoDesactivable === MOTIVOS_DE_BAJA.CONSUMIDOR_NO_DISPONIBLE) {
    return {
      presentacion: PRESENTACION_DE_LA_BAJA.DESHABILITADA,
      texto: 'No se pudo calcular el saldo de esta cuenta, así que no se puede saber si la baja procede. Vuelve a intentarlo.',
      salida: null,
      rotuloDelAtajo: null,
      ofreceReintento: true,
    };
  }

  // CUENTA_CON_SALDO · tres redacciones, una por situación (RN-546).
  const pendiente = Number(cuenta.importePendiente ?? NaN);
  const divisa = cuenta.codigoISO;
  const absoluto = Number.isFinite(pendiente)
    ? formatearImporte(Math.abs(pendiente).toFixed(2), divisa)
    : formatearImporte('0.00', divisa);

  let texto;
  if (cuenta.esCredito) {
    texto = pendiente < 0
      ? `No se puede desactivar: tiene ${absoluto} a tu favor.`
      : `No se puede desactivar: todavía debe ${absoluto}.`;
  } else {
    texto = pendiente < 0
      ? `No se puede desactivar: tiene ${absoluto} a tu favor.`
      : `No se puede desactivar: todavía tiene ${absoluto}.`;
  }

  // RN-546a · No se promete lo que no se cumple. Sin movimientos, dejar el saldo
  // inicial en cero deja el saldo en cero y el atajo puede decirlo. Con
  // movimientos, corregirlo cambia el saldo pero rara vez lo deja en cero exacto.
  const sinMovimientos = (cuenta.cantidadMovimientos ?? 0) === 0;
  const rotuloDelAtajo = sinMovimientos
    ? 'Corregir el saldo inicial a cero'
    : (cuenta.esCredito ? 'Corregir la deuda inicial' : 'Corregir el saldo inicial');

  return {
    presentacion: PRESENTACION_DE_LA_BAJA.DESHABILITADA,
    texto,
    salida: 'Puedes corregir su saldo inicial o dejarla en cero con movimientos.',
    rotuloDelAtajo,
    ofreceReintento: false,
  };
}
